import React, { useRef, useState, useEffect } from 'react';
import { CameraIcon, PlusIcon } from '@heroicons/react/24/outline';

interface ImageCameraDialogProps {
  onImageSelected: (file: File, preview: string) => void;
  onCancel?: () => void;
  className?: string;
  label?: string;
}

const ImageCameraDialog: React.FC<ImageCameraDialogProps> = ({
  onImageSelected,
  className = '',
  label = 'Image (Optional)'
}) => {
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Reset captured image when component mounts (useful when key prop changes)
  useEffect(() => {
    setCapturedImage(null);
    setError(null);
  }, []);

  const resetImageSelection = () => {
    setCapturedImage(null);
    setError(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;
        setCapturedImage(preview);
        onImageSelected(file, preview);
      };
      reader.readAsDataURL(file);
      // Reset the input value to allow selecting the same file again
      e.target.value = '';
    }
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-body mb-2">
        {label}
      </label>

      {/* Error display */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-1 text-sm text-red-600 hover:text-red-800 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Image preview */}
      {capturedImage && (
        <div className="mb-4 text-center">
          <img
            src={capturedImage}
            alt="Selected"
            className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-edge-input"
          />
          <button
            type="button"
            onClick={resetImageSelection}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            Remove Photo
          </button>
        </div>
      )}

      {/* Image selection options */}
      {!capturedImage && (
        <div className="space-y-3">
          <div className="flex space-x-3">
            <label className="flex-1 flex items-center justify-center px-4 py-2 border border-edge-input rounded-md shadow-sm bg-surface text-sm font-medium text-body hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer">
              <CameraIcon className="h-4 w-4 mr-2" />
              Take Photo
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
            <label className="flex-1 flex items-center justify-center px-4 py-2 border border-edge-input rounded-md shadow-sm bg-surface text-sm font-medium text-body hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer">
              <PlusIcon className="h-4 w-4 mr-2" />
              Choose File
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCameraDialog;
